import { onMounted, ref, watch, type Ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import {
  resolveNextToolbarFitState,
  type ToolbarFitState,
  type ToolbarStateWidths,
} from '@/composables/toolbarFitState';

/**
 * Drives the full → icons → collapsed ladder from real measurements.
 *
 * The pure decision lives in {@link resolveNextToolbarFitState}; this is the
 * loop around it — measure, step one state, re-measure once the DOM settles.
 * Room decides, not viewport breakpoints, because the toolbars that need this
 * sit in boxes the window knows nothing about: the inspector panel is a
 * user-resizable sheet, and the sidebar can widen under a search page without
 * any window resize to notice.
 *
 * Two elements, because "how much room is there" and "how wide is the content"
 * are not always the same box:
 *
 *  - `roomRef` is the element whose width IS the room available. It must be a
 *    full-width box, so its `clientWidth` reports the room whether or not the
 *    content currently fits.
 *  - `contentRef` is the cluster being collapsed; its `scrollWidth` is what
 *    that cluster needs. Defaults to `roomRef` when they are the same element.
 *
 * Measuring a shrink-to-fit cluster against ITSELF is the trap this split
 * exists to avoid: once such a cluster stops overflowing, its box collapses
 * onto its own content, so `clientWidth === scrollWidth` forever and the
 * ladder can never promote back up when the room returns.
 */
export interface UseToolbarFitOptions {
  /** Full-width box whose `clientWidth` is the room available. */
  roomRef: Ref<HTMLElement | null>;
  /** Cluster whose `scrollWidth` is the room needed. Defaults to `roomRef`. */
  contentRef?: Ref<HTMLElement | null>;
  /**
   * Everything the measured widths depend on besides the box itself — label
   * text, which controls are present. The cached per-state widths are only
   * valid for what they were measured with, so a change here drops them and
   * re-learns from `full`. The whole loop runs in pre-paint microtasks, so
   * there is no visible flash.
   */
  labelSignature?: Ref<string>;
  /**
   * Controls inside the content whose width can change without the room or the
   * content box resizing, such as the Sync pill's counts growing from "7 · 5"
   * to "17 · 15" in a full-width row. Each one is observed, and a resize
   * measures again.
   */
  observedControlRefs?: Array<Ref<HTMLElement | null>>;
}

export function useToolbarFit(options: UseToolbarFitOptions) {
  const { roomRef, contentRef = roomRef, labelSignature, observedControlRefs = [] } = options;

  const fitState = ref<ToolbarFitState>('full');

  /**
   * scrollWidth each expanded state needed the last time it was rendered —
   * recorded on every measure of that state, read by promotion decisions.
   * (While a state *fits*, scrollWidth reports the box rather than the
   * narrower content width, so the cache is conservative: promotion may need a
   * few px more room than strictly necessary, never less.)
   */
  let cachedStateWidths: ToolbarStateWidths = {};

  function measure(): void {
    const roomEl = roomRef.value;
    const contentEl = contentRef.value;
    if (!roomEl || !contentEl) return;

    const currentState = fitState.value;
    const scrollWidth = contentEl.scrollWidth;
    const clientWidth = roomEl.clientWidth;

    if (currentState !== 'collapsed') {
      cachedStateWidths[currentState] = scrollWidth;
    }

    const nextState = resolveNextToolbarFitState({
      currentState,
      scrollWidth,
      clientWidth,
      cachedStateWidths,
    });

    // Only act on a real transition — a state write triggers relayout and
    // another measure pass, so an unconditional write would loop.
    if (nextState !== currentState) {
      fitState.value = nextState;
    }
  }

  /**
   * Re-measure once the state change is actually on the screen: the new layout
   * can itself change what fits, so the ladder walks one step per pass until it
   * settles.
   *
   * `flush: 'post'` is load-bearing, not tidiness. A `nextTick(measure)` from a
   * default (pre-flush) watcher can run while the DOM still holds the PREVIOUS
   * state — and it does, whenever the state was written from inside another
   * `nextTick` callback, which is exactly what the label-signature reset below
   * does. The measure then reads the old markup: the toolbar was told it was in
   * `icons` while `scrollWidth` still reported the full labelled row (447px in
   * a 280px panel), so it demoted a second time and collapsed everything into
   * the overflow menu with room to spare for the icon row. A post-flush watcher
   * runs after Vue has patched the DOM, so the number belongs to the state
   * being measured.
   */
  watch(fitState, measure, { flush: 'post' });
  useResizeObserver(roomRef, measure);
  if (contentRef !== roomRef) {
    useResizeObserver(contentRef, measure);
  }
  for (const controlRef of observedControlRefs) {
    useResizeObserver(controlRef, measure);
  }
  onMounted(measure);

  if (labelSignature) {
    watch(
      labelSignature,
      () => {
        cachedStateWidths = {};
        // Already at the widest state, and this callback runs after the new
        // labels are on screen — so the layout in front of us is the one to
        // measure. Otherwise the reset re-renders and the post-flush watcher
        // above measures the layout that produces; measuring here would read
        // the state we are leaving.
        if (fitState.value === 'full') {
          measure();
          return;
        }
        fitState.value = 'full';
      },
      { flush: 'post' },
    );
  }

  return { fitState, measure };
}
